<template>
  <div
    class="ms-getting-started-mini"
    id="mgettingstartedmini"
    :class="{'is-dragging': isDragging, 'position-adjusted': isAdjusted}"
    :style="positionStyle"
    ref="miniPopupRef"
  >
    <!-- Header -->
    <div class="mini-header" :class="{'drag-handle': draggable}" @mousedown="onMouseDown" @click="handleHeaderClick">
      <div class="header-content">
        <!-- <div class="header-icon mr-1">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M10 17.5V14.1667M10 10.8333V7.5M10 4.16667V2.5M8.33337 17.5H11.6667M6.66671 4.16667V7.5H15.8334L17.5 5.83333L15.8334 4.16667H6.66671ZM11.6667 10.8333V14.1667H5.00004L3.33337 12.5L5.00004 10.8333H11.6667Z"
                stroke="white"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div> -->
        <div class="header-text">
          <div class="header-title">{{ defaultTitleTextDisplay }}</div>
          <div class="header-progress" id="headerProgressText">{{ progressTextDisplay }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import popupUtil from '@/commons/popupUtil';
import commonFn from '@/commons/commonFunction';
import {useDraggable} from '@/composables/useDraggable';
import {usePopupCollisionDetector} from '@/composables/usePopupCollisionDetector';
import {ref, watch, computed, onMounted} from 'vue';
import GettingStartedApi from '@/apis/di/gettingStartedApi';
import {useContextStore} from '@/stores/contextStore';
import {MSConstant} from '@/commons/wildcards/index.js';
import {useDbOptionStore} from '@/stores/dbOptionStore.js';

export default {
  name: 'MsGettingStartedMini',
  props: {
    progressText: {
      type: String,
      default: '0/3',
    },
    draggable: {
      type: Boolean,
      default: true,
    },
    initialPosition: {
      type: Object,
      default: () => ({top: '50%', right: 0, transform: 'translateY(-50%)'}),
    },
  },

  setup(props, {emit}) {
    const contextStore = useContextStore();
    const dbOptionStore = useDbOptionStore();

    const isAlreadyBuyQlcb = contextStore?.$state?.context?.Context?.IsAlreadyBuyQlcb;
    const isAlreadyBuyMimosa = contextStore?.$state?.context?.Context?.IsAlreadyBuyMimosa;
    const isAlreadyBuyBumas = contextStore?.$state?.context?.Context?.IsAlreadyBuyBumas;
    const isAlreadyBuySalagov = contextStore?.$state?.context?.Context?.IsAlreadyBuySalagov;
    const isAlreadyBuyQlts = contextStore?.$state?.context?.Context?.IsAlreadyBuyQlts;

    const isGettingNew = ref(
      !(isAlreadyBuyQlcb || isAlreadyBuyMimosa || isAlreadyBuyBumas || isAlreadyBuySalagov || isAlreadyBuyQlts),
    );

    const hasAdditionalPurchaseAppDefault = ref(false);
    const progressTextCount = ref('0/3');
    const defaultTitle = ref('Bắt đầu sử dụng');
    const isConfigLoaded = ref(false); // Flag to ensure we only load config once
    // Track drag state to prevent click after drag
    const justFinishedDrag = ref(false);
    // Use draggable composable
    const {
      elementRef: miniPopupRef,
      isDragging,
      positionStyle,
      startDrag,
      resetPosition,
      position,
    } = useDraggable({
      initialPosition: props.initialPosition,
      enabled: props.draggable,
      storageKey: 'miniGettingStartedPosition',
      snapThreshold: 50,
      boundaryPadding: 10,
      onDragEnd: () => {
        // Set flag when drag ends to prevent immediate click
        justFinishedDrag.value = true;
        // Reset flag after a short delay
        setTimeout(() => {
          justFinishedDrag.value = false;
        }, 100);
      },
    });

    const progressTextDisplay = computed(() => {
      if (hasAdditionalPurchaseAppDefault.value) {
        return '';
      }
      if (window?.isShowMiniGetting) {
        return progressTextCount.value;
      }
      return props.progressText;
    });

    const defaultTitleTextDisplay = computed(() => {
      return defaultTitle.value;
    });

    // Collision detection and position adjustment
    const {maximizedPopups, checkCollisionWithMiniPopup} = usePopupCollisionDetector();
    const originalPosition = ref(null);
    const isAdjusted = ref(false);

    // Simple mouse down handler
    const onMouseDown = e => {
      if (!props.draggable) return;
      startDrag(e);
    };

    // Handle header click - only if not just finished dragging
    const handleHeaderClick = e => {
      // Prevent click if we just finished dragging
      if (justFinishedDrag.value || isDragging.value) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      if (hasAdditionalPurchaseAppDefault.value) {
        toggleShowPopupAdditionalPurchaseApp();
        return;
      }

      toggleGettingStarted();
    };

    // Original toggle function
    // [htthoa - 2026-06-30] Sửa: kiểm tra is_start_getting từ dbOption trước khi điều hướng iGOV
    const toggleGettingStarted = async () => {
      if (contextStore?.$state?.context?.Context?.PlatformType == MSConstant.PlatformType.iHOS) {
        if (isGettingNew.value) {
          // isGettingNew.value = false; // fake tạm data
          // nếu trường hợp mua mới thì show GettingStartedPopup
          popupUtil.show('GettingStartedPopup');
        } else {
          await getStepGettingStarted();
          // đặt tên cho popup nâng cấp iHos
          popupUtil.show('UpgradeGettingStartedPopup', {
            stepGettingStarted: stepGettingStarted.value,
          });
        }
      } else if (contextStore?.$state?.context?.Context?.PlatformType == MSConstant.PlatformType.iGOV) {
        const userId = contextStore?.$state?.context?.user?.user_id;
        const tenantId = contextStore?.$state?.context?.Context?.TenantId;
        let isStartGetting = dbOptionStore.getDbOptionValue('is_start_getting');
        if (isStartGetting == null) {
          const dbOptions = await dbOptionStore.getDbOptions();
          const gettingOpt = dbOptions?.find(
            o => o.option_name === 'is_start_getting' && o.user_id == userId && o.tenant_id == tenantId,
          );
          isStartGetting = gettingOpt?.option_value ?? null;
        }
        if (localStorage.getItem('isCloseGetting')) {
          localStorage.removeItem('isCloseGetting');
        }
        if (isStartGetting === 'true') {
          if(commonFn.isMergeTenantIGOV()){
            window.location.href = '/system/getting-merger';
          } else {
            window.location.href = '/system/getting';
          }
        } else {
          if(commonFn.isMergeTenantIGOV()) {
            popupUtil.show('GettingStartedMergerBanner');
          } else {
            popupUtil.show('GettingStartedBanner');
          }
        }
      }
      commonFn.hideMiniGettingStarted();
    };

    const toggleShowPopupAdditionalPurchaseApp = async () => {
      try {
        // Lấy config đã lưu từ localStorage
        const savedConfigStr = localStorage.getItem('NotificationPopupiGov_Config');

        if (savedConfigStr) {
          const savedConfig = JSON.parse(savedConfigStr);

          // Hiển thị popup với config đã lưu
          popupUtil.show('NotificationPopupiGov', {
            config: savedConfig,
          });
        } else {
          console.warn('Không tìm thấy config đã lưu cho NotificationPopupiGov');
          // Có thể hiển thị popup với config mặc định hoặc thông báo lỗi
        }
      } catch (error) {
        console.error('Lỗi khi load config popup:', error);
      }

      commonFn.hideMiniGettingStarted();
    };

    const stepGettingStarted = ref([]);
    const getStepGettingStarted = async () => {
      const res = await GettingStartedApi.getCompletionStatus();
      if (res) {
        stepGettingStarted.value = res;
      }
    };
    // Watch for maximized popups and adjust position accordingly
    watch(
      () => maximizedPopups.value.size,
      newSize => {
        setTimeout(() => {
          if (newSize > 0) {
            // Maximized popup opened - check for collision
            adjustPositionForCollision();
          } else {
            // No maximized popup - restore original position
            restoreOriginalPosition();
          }
        }, 200);
      },
      {immediate: true},
    );

    /**
     * Adjust position if mini popup collides with footer area
     */
    const adjustPositionForCollision = () => {
      if (!miniPopupRef.value || isAdjusted.value) return;

      // Check if there's actually a collision before adjusting
      const hasCollision = checkCollisionWithMiniPopup(miniPopupRef.value);

      if (hasCollision) {
        // Save original position before adjusting
        originalPosition.value = {...position.value};

        // Adjust position - move up by 32px to ensure clearance
        const adjustment = 32;
        if (position.value.bottom !== undefined) {
          position.value.bottom = position.value.bottom + adjustment;
        } else if (position.value.top !== undefined && typeof position.value.top === 'number') {
          position.value.top = position.value.top - adjustment;
        }
        isAdjusted.value = true;
      }
    };

    /**
     * Restore original position when no maximized popup is open
     */
    const restoreOriginalPosition = () => {
      if (!isAdjusted.value || !originalPosition.value) return;

      // Restore original position
      position.value = {...originalPosition.value};
      isAdjusted.value = false;
      originalPosition.value = null;
    };

    // Backup method: Use MutationObserver to detect maximized popups in DOM
    const observeMaximizedPopups = () => {
      const observer = new MutationObserver(() => {
        checkVisibility();
        let hasMaximizedPopup = false;

        // Check for popups with maximum style (width: 100%, height: 100vh)
        const popups = document.querySelectorAll('.modal-content');
        popups.forEach(popup => {
          const styles = window.getComputedStyle(popup);
          if (styles.width === '100%' && (styles.height === '100vh' || styles.height === window.innerHeight + 'px')) {
            hasMaximizedPopup = true;
          }
        });

        // Check for any popup that needs mini collision detection
        const collisionPopup = document.querySelector('.mini-collision-popup');
        if (collisionPopup) {
          hasMaximizedPopup = true;
        }

        // Trigger adjustment if maximized popup found and not already adjusted
        if (hasMaximizedPopup && !isAdjusted.value) {
          adjustPositionForCollision();
        } else if (!hasMaximizedPopup && isAdjusted.value) {
          restoreOriginalPosition();
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class'],
      });

      return observer;
    };

    // Watch for visibility changes - khi component hiển thị thì load config
    const checkVisibility = () => {
      if (miniPopupRef.value) {
        const isVisible = window.getComputedStyle(miniPopupRef.value).display !== 'none';
        if (isVisible && !isConfigLoaded.value) {
          // Component đang visible và chưa load config -> load ngay
          loadAdditionalPurchaseAppConfig();
        }
      }
    };
    /**
     * @description: Load additional purchase app config from dbOptions
     * @return: {Promise<void>}
     * CreatedBy: dkdien 12.3.2026
     */
    const loadAdditionalPurchaseAppConfig = async () => {
      // Chỉ load một lần duy nhất
      if (isConfigLoaded.value) return;

      try {
        var dbOptions = dbOptionStore._dbOptionsCache;

        if (!dbOptions || !Array.isArray(dbOptions)) {
          dbOptions = await dbOptionStore.getDbOptions();
        }
        const hasAdditionalPurchaseApps =
          dbOptions?.find(option => option.option_name === 'has_additional_purchase_apps')?.option_value === 'true';

        hasAdditionalPurchaseAppDefault.value = hasAdditionalPurchaseApps;
        if (hasAdditionalPurchaseApps) {
          defaultTitle.value = 'Cấp mới ứng dụng';
        }

        isConfigLoaded.value = true; // Đánh dấu đã load xong
      } catch (error) {
        console.error('Lỗi khi load dbOptions:', error);
      }
    };

    onMounted(async () => {
      // Check if there are already maximized popups when this component mounts
      if (maximizedPopups.value.size > 0) {
        setTimeout(() => {
          adjustPositionForCollision();
        }, 300);
      }
      progressTextCount.value = props.progressText;
      //xử lý nếu cho phép hiện mini getting started từ window.isShowMiniGetting
      if (window?.isShowMiniGetting) {
        commonFn.mask();
        var step1 = await GettingStartedApi.getGettingStartedStep();

        // update setup steps
        var response = await GettingStartedApi.getCompletionStatus();
        //nếu được mở từ form đồng bộ thì hiện miniGetting
        var completeCount = 0;
        if (step1.success && step1.data) {
          // Kiểm tra xem step1.data có step_type = 1 thì insert vào response 1 dòng có trường step=1, completed = is_complete trong steps 1
          var step1Data = step1.data.filter(item => item.step_type === 1 && item.is_completed);
          if (step1Data && step1Data.length > 0) {
            completeCount = 1;
          }
        }

        if (response) {
          var countStepCompleted = response.filter(item => item.completed);
          if (countStepCompleted && countStepCompleted.length > 0) {
            completeCount += countStepCompleted.length;
          }
        }
        progressTextCount.value = `${completeCount}/3`;
        commonFn.unmask();
      }

      // const dbOptions = await loadDbOptionsOnce();
      // const hasAdditionalPurchaseApps =
      //   dbOptions?.find(option => option.option_name === 'has_additional_purchase_apps')?.option_value ===
      //   'true';

      //   hasAdditionalPurchaseAppDefault.value = hasAdditionalPurchaseApps;
      //   if(hasAdditionalPurchaseApps){
      //     defaultTitle.value = 'Cấp mới ứng dụng';
      //   }
      // Start DOM observation as backup
      const observer = observeMaximizedPopups();

      // Cleanup observer when component unmounts
      return () => {
        observer.disconnect();
      };
    });

    /**
     * @description: Load dbOptions một lần duy nhất
     * @return: {Promise<Array>} dbOptions
     * CreatedBy: ndquan1 28.7.2025
     */
    const loadDbOptionsOnce = async () => {
      try {
        return await dbOptionStore.getDbOptions();
      } catch (error) {
        console.error('Lỗi khi load dbOptions:', error);
        return [];
      }
    };

    return {
      miniPopupRef,
      isDragging,
      positionStyle,
      onMouseDown,
      handleHeaderClick,
      toggleGettingStarted,
      resetPosition,
      isAdjusted,
      progressTextCount,
      progressTextDisplay,
      defaultTitleTextDisplay,
    };
  },
};
</script>

<style lang="scss" scoped>
.ms-getting-started-mini {
  position: fixed;
  border-radius: 8px;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  border: none;
  z-index: 9999;
  transition: all 0.3s ease;
  overflow: hidden;
  cursor: default;

  &.is-dragging {
    transition: none;
    z-index: 10000;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    transform: rotate(0.5deg);

    .mini-header {
      cursor: grabbing !important;
    }
  }

  &.minimized {
    .mini-content {
      display: none;
    }
  }

  &.position-adjusted {
    // Visual indicator when position is adjusted for collision avoidance
    box-shadow: 0 -4px 20px rgba(37, 99, 235, 0.2);
  }

  .mini-header {
    display: flex;
    padding: 6px 16px;
    align-items: center;
    border-radius: 8px;
    background: $primary;
    box-shadow: 0px 2px 8px 0px rgba(0, 0, 0, 0.25);
    justify-content: space-between;
    cursor: pointer;
    border-bottom: none;
    user-select: none;

    &.drag-handle {
      cursor: grab;

      &:hover {
        background: $primary-hover;
      }

      &:active {
        cursor: grabbing;
      }
    }

    &:hover {
      background: $primary-hover;
    }

    .header-content {
      display: flex;
      align-items: center;
      gap: 8px;

      .header-icon {
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;

        svg {
          width: 20px;
          height: 20px;
        }
      }

      .header-text {
        display: flex;
        align-items: center;
        gap: 8px;

        .header-title {
          color: #fff;
          text-align: center;
          font-size: 14px;
          font-style: normal;
          font-weight: 500;
          line-height: 20px;
        }

        .header-progress {
          display: flex;
          padding: 3px 2px 3px 4px;
          justify-content: center;
          align-items: center;
          border-radius: 8px;
          background: #fff;
          font-size: 12px;
          color: #374151;
        }
      }
    }

    .header-controls {
      display: flex;
      align-items: center;
      gap: 8px;

      .control-icon {
        width: 16px;
        height: 16px;
        cursor: pointer;
        color: #ffffff;
        transition: color 0.2s ease;

        &:hover {
          color: #e5e7eb;
        }

        &.close-icon:hover {
          color: #fecaca;
        }
      }
    }
  }

  .mini-content {
    padding: 16px;
    max-height: 400px;
    overflow-y: auto;
    background: #ffffff;

    .progress-section {
      margin-bottom: 16px;

      .progress-bar {
        width: 100%;
        height: 4px;
        background: #e5e7eb;
        border-radius: 2px;
        overflow: hidden;
        margin-bottom: 8px;

        .progress-fill {
          height: 100%;
          transition: width 0.3s ease;
          border-radius: 2px;

          &.progress-start {
            background: #ef4444;
          }
          &.progress-low {
            background: #f59e0b;
          }
          &.progress-medium {
            background: #eab308;
          }
          &.progress-high {
            background: #22c55e;
          }
          &.progress-complete {
            background: #10b981;
          }
        }
      }

      .progress-text-detail {
        font-size: 12px;
        color: #6b7280;
        text-align: center;
      }
    }

    .steps-list {
      margin-bottom: 16px;

      .step-item {
        display: flex;
        align-items: flex-start;
        padding: 12px 0;
        border-bottom: 1px solid #f3f4f6;

        &:last-child {
          border-bottom: none;
        }

        &.completed {
          .step-status-icon {
            color: #10b981;
          }

          .step-title {
            color: #6b7280;
            text-decoration: line-through;
          }
        }

        &.invalid-sync {
          .step-status-icon {
            color: #ef4444;
          }
        }

        .step-status-icon {
          width: 16px;
          height: 16px;
          margin-top: 2px;
          flex-shrink: 0;
          color: #d1d5db;
        }

        .step-info {
          flex: 1;

          .step-title {
            font-size: 13px;
            font-weight: 500;
            color: #1f2937;
            margin-bottom: 8px;
            line-height: 1.3;
          }

          .step-actions {
            display: flex;
            gap: 8px;

            .action-btn {
              display: flex;
              align-items: center;
              gap: 4px;
              padding: 4px 8px;
              border: 1px solid #d1d5db;
              border-radius: 4px;
              background: #ffffff;
              font-size: 11px;
              color: #6b7280;
              cursor: pointer;
              transition: all 0.2s ease;

              &:hover {
                border-color: #6366f1;
                color: #6366f1;
              }

              &.setup-btn {
                background: #6366f1;
                color: #ffffff;
                border-color: #6366f1;

                &:hover {
                  background: #5b5adb;
                }
              }
            }
          }
        }
      }
    }

    .action-buttons {
      display: flex;
      gap: 8px;

      .btn-full-view,
      .btn-complete {
        flex: 1;
        padding: 8px 12px;
        border-radius: 6px;
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
      }

      .btn-full-view {
        background: #ffffff;
        border: 1px solid #d1d5db;
        color: #374151;

        &:hover {
          border-color: #6366f1;
          color: #6366f1;
        }
      }

      .btn-complete {
        background: #10b981;
        border: 1px solid #10b981;
        color: #ffffff;

        &:hover {
          background: #059669;
        }
      }
    }
  }
}

// Scrollbar styling
.mini-content::-webkit-scrollbar {
  width: 4px;
}

.mini-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

.mini-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.mini-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
.icon-chevron-up {
  width: 20px;
  height: 20px;
  background-image: url('@/assets/images/icon-chevron-up.svg');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}
</style>
